import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Pencil, Trash2 } from "lucide-react";
import { fmtLocalDT } from "@/lib/clinical";
import { useAuth } from "@/lib/auth";

const RANGES = {
  temperature_c: [36.5, 37.5],
  heart_rate: [110, 170],
  resp_rate: [30, 60],
  spo2: [92, 100],
  blood_sugar: [45, 150],
};
function abnormalCls(k, v) {
  const r = RANGES[k]; if (!r || v == null || v === "") return "";
  const n = Number(v); if (isNaN(n)) return "";
  if (n > r[1]) return "abnormal-high";
  if (n < r[0]) return "abnormal-low";
  return "";
}

const NON_NUMERIC_KEYS = new Set(["stool", "vomiting", "remarks"]);
const fToC = (f) => (Number(f) - 32) * (5 / 9);
const cToF = (c) => Number(c) * (9 / 5) + 32;

const FIELDS = [
  ["temperature_c", "Temp"],
  ["heart_rate", "HR"],
  ["resp_rate", "RR"],
  ["spo2", "SpO₂"],
  ["bp_sys", "BPs"],
  ["bp_dia", "BPd"],
  ["crt_sec", "CRT s"],
  ["weight_g", "Wt g"],
  ["blood_sugar", "Sugar"],
  ["urine_output_ml", "Urine ml"],
];

export default function VitalsTab({ patient }) {
  const { user } = useAuth();
  // Vitals are entered by anyone; only doctor/admin can retroactively fix typos or delete
  // wrong entries, matching the request "doctor can edit vitals if there is typing mistake".
  const canEdit = ["doctor", "admin", "nurse"].includes(user?.role);
  const canDelete = ["doctor", "admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tempUnit, setTempUnit] = useState(() => localStorage.getItem("nicu.tempUnit") || "C");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(
    () => api.get(`/vitals/${patient.id}`).then(({ data }) => setRows(data)).catch(() => {}),
    [patient.id]
  );
  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem("nicu.tempUnit", tempUnit); }, [tempUnit]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { patient_id: patient.id };
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        if (NON_NUMERIC_KEYS.has(k)) { payload[k] = v; return; }
        if (k === "temperature_c" && tempUnit === "F") { payload[k] = Number(fToC(v).toFixed(2)); return; }
        payload[k] = Number(v);
      });
      await api.post("/vitals", payload);
      setForm({});
      toast.success("Vitals recorded");
      load();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const displayTemp = (c) => {
    if (c == null || c === "") return "";
    return tempUnit === "F" ? Number(cToF(c)).toFixed(1) : c;
  };

  const submitEdit = async (updates) => {
    try {
      await api.patch(`/vitals/${editing.id}`, updates);
      toast.success("Vitals updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const submitDelete = async () => {
    try {
      await api.delete(`/vitals/${deleting.id}`);
      toast.success("Entry removed");
      setDeleting(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const chartHR = rows.slice().reverse().map(r => ({ t: (r.at || "").slice(11, 16), hr: r.heart_rate ?? null, spo2: r.spo2 ?? null }));
  const chartW = rows.filter(r => r.weight_g).slice().reverse().map(r => ({ t: (r.at || "").slice(5, 10), w: r.weight_g }));

  return (
    <div className="space-y-6" data-testid="vitals-tab">
      {/* Entry row */}
      <div className="card-clinical p-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">New vitals entry</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] text-muted-foreground">
                  {k === "temperature_c" ? `${l} (°${tempUnit})` : l}
                </label>
                {k === "temperature_c" && (
                  <div className="inline-flex rounded-full border border-border overflow-hidden text-[10px]" data-testid="temp-unit-toggle">
                    {["C", "F"].map((u) => (
                      <button
                        key={u}
                        type="button"
                        onClick={() => setTempUnit(u)}
                        className={`px-1.5 leading-4 transition-colors ${tempUnit === u ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        data-testid={`temp-unit-${u.toLowerCase()}`}
                      >°{u}</button>
                    ))}
                  </div>
                )}
              </div>
              <Input type="number" step="0.1" value={form[k] || ""} onChange={(e) => setForm(s => ({ ...s, [k]: e.target.value }))} data-testid={`vitals-${k}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4">
          <Button onClick={save} disabled={saving} className="rounded-full" data-testid="vitals-save">{saving ? "Saving…" : "Record"}</Button>
        </div>
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-clinical p-4">
          <div className="text-sm font-medium mb-2">Heart rate & SpO₂ trend</div>
          <div className="h-40">
            <ResponsiveContainer><LineChart data={chartHR}>
              <XAxis dataKey="t" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="hr" stroke="#EF4444" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="spo2" stroke="#0284C7" strokeWidth={2} dot={false} />
            </LineChart></ResponsiveContainer>
          </div>
        </div>
        <div className="card-clinical p-4">
          <div className="text-sm font-medium mb-2">Weight trend (g)</div>
          <div className="h-40">
            <ResponsiveContainer><LineChart data={chartW}>
              <XAxis dataKey="t" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} domain={["auto", "auto"]} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="w" stroke="#10B981" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart></ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* History table */}
      <div className="card-clinical overflow-auto">
        <table className="vitals-grid w-full tabular">
          <thead>
            <tr>
              <th>Time</th>
              {FIELDS.map(([k, l]) => <th key={k}>{k === "temperature_c" ? `${l} (°${tempUnit})` : l}</th>)}
              <th>By</th>
              {(canEdit || canDelete) && <th className="text-right w-20">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={FIELDS.length + 3} className="text-center text-muted-foreground py-6">No entries yet</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`vital-row-${r.id}`}>
                <td className="whitespace-nowrap">{fmtLocalDT(r.at)}</td>
                {FIELDS.map(([k]) => (
                  <td key={k} className={abnormalCls(k, r[k])}>
                    {k === "temperature_c" ? displayTemp(r[k]) : (r[k] ?? "")}
                  </td>
                ))}
                <td className="text-muted-foreground text-xs">{r.by}{r.updated_by && r.updated_by !== r.by && <span title={`Edited by ${r.updated_by}`}> *</span>}</td>
                {(canEdit || canDelete) && (
                  <td className="text-right whitespace-nowrap">
                    {canEdit && (
                      <button className="h-6 w-6 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-primary"
                              title="Fix typo" onClick={() => setEditing(r)} data-testid={`vital-edit-${r.id}`}>
                        <Pencil size={11} />
                      </button>
                    )}
                    {canDelete && (
                      <button className="h-6 w-6 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-destructive"
                              title="Delete wrong entry" onClick={() => setDeleting(r)} data-testid={`vital-delete-${r.id}`}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditVitalModal
          row={editing}
          tempUnit={tempUnit}
          onClose={() => setEditing(null)}
          onSave={submitEdit}
        />
      )}
      {deleting && (
        <Dialog open onOpenChange={() => setDeleting(null)}>
          <DialogContent data-testid="vital-delete-modal">
            <DialogHeader>
              <DialogTitle className="text-destructive flex items-center gap-2"><Trash2 size={16}/> Delete this vital entry?</DialogTitle>
              <DialogDescription>
                Recorded at {fmtLocalDT(deleting.at)} by {deleting.by}. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
              <Button variant="destructive" onClick={submitDelete} data-testid="vital-delete-confirm">Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function EditVitalModal({ row, tempUnit, onClose, onSave }) {
  // Show temperature in the current user unit; convert back to °C on save.
  const [f, setF] = useState(() => {
    const out = {};
    FIELDS.forEach(([k]) => { out[k] = row[k] ?? ""; });
    if (out.temperature_c !== "" && out.temperature_c != null && tempUnit === "F") {
      out.temperature_c = Number(cToF(out.temperature_c)).toFixed(1);
    }
    return out;
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    const payload = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v === "" || v == null) { payload[k] = null; return; }
      if (NON_NUMERIC_KEYS.has(k)) { payload[k] = v; return; }
      if (k === "temperature_c" && tempUnit === "F") { payload[k] = Number(fToC(v).toFixed(2)); return; }
      payload[k] = Number(v);
    });
    // strip nulls — leave those fields untouched (VitalUpdate uses exclude_none)
    Object.keys(payload).forEach(k => { if (payload[k] === null) delete payload[k]; });
    await onSave(payload);
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="vital-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil size={16}/> Edit vital entry</DialogTitle>
          <DialogDescription>Recorded {fmtLocalDT(row.at)} by {row.by}. Fix typing mistakes; changes are audit-logged.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1">
              <label className="text-[11px] text-muted-foreground">{k === "temperature_c" ? `${l} (°${tempUnit})` : l}</label>
              <Input type="number" step="0.1" value={f[k] ?? ""} onChange={(e) => setF(s => ({ ...s, [k]: e.target.value }))}
                     data-testid={`vital-edit-${k}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="vital-edit-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
